var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { of, forkJoin } from 'rxjs';
import { CameraService } from '../engine/camera.service';
import { NumberService } from '../shared/number.service';
import { WidgetOptions } from './models/widget-options.model';
import { flatMap, map, catchError } from 'rxjs/operators';
import { ExportService } from '../shared/export.service';
import { saveAs } from 'file-saver';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { ImageService } from '../shared/image.service';
import { defaultScreen } from './data/screens';
import { get } from 'lodash';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "../engine/camera.service";
import * as i3 from "../shared/number.service";
import * as i4 from "../shared/export.service";
import * as i5 from "../shared/image.service";
var formHeaders = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json; charset=utf-8'
    })
};
var defaultOptions = new WidgetOptions();
export var PublishLinkType;
(function (PublishLinkType) {
    PublishLinkType["Single"] = "share.widget.link";
    PublishLinkType["Bulk"] = "share.widget.export";
})(PublishLinkType || (PublishLinkType = {}));
var embedDefaults = {
    id: 'embedded-human',
    dimensions: defaultScreen.dimensions,
    fullScreen: true
};
var PublishService = /** @class */ (function () {
    function PublishService(http, cameraService, numberService, exportService, imageService) {
        this.http = http;
        this.cameraService = cameraService;
        this.numberService = numberService;
        this.exportService = exportService;
        this.imageService = imageService;
    }
    PublishService.prototype.exportImage = function (imageData, contentType, fileName) {
        var exportBlob = this.imageService.base64ToBlob(imageData, contentType);
        saveAs(exportBlob, fileName);
        return of(true);
    };
    PublishService.prototype.exportModels = function (models, widgetPath, widgetOptions, dimensions, lang, app, offlineEnabled) {
        var _this = this;
        if (offlineEnabled === void 0) { offlineEnabled = false; }
        // default export columns
        var userExportColumns = [
            { label: 'Title', field: 'title' },
            { label: 'URL', field: 'url' },
            { label: 'iFrame', field: 'iFrame' },
            { label: 'Author', field: 'author' },
            { label: 'Thumbnail URL', field: 'thumbnail_url' }
        ];
        // optional export columns
        if (offlineEnabled) {
            userExportColumns.push({
                label: 'Offline Package URL',
                field: 'offline_url'
            });
        }
        // get export models
        return this.getExportModels(models, widgetPath, widgetOptions, dimensions, lang, app, offlineEnabled)
            .pipe(
        // export models -> csv
        flatMap(function (exportedModels) { return _this.exportService.exportCSV(exportedModels, userExportColumns); }), 
        // download csv
        map(function (csv) {
            // get formatted export name (YYYY-MM-DD)
            var isoDate = new Date().toISOString().split('T')[0];
            var exportFileName = "BioDigital-Widgets-" + isoDate + "-export.csv";
            var exportBlob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
            saveAs(exportBlob, exportFileName);
            return true;
        }));
    };
    // Generates a list of export models per bookmark
    PublishService.prototype.getExportModels = function (bookmarks, widgetPath, widgetOptions, dimensions, lang, app, offlineEnabled) {
        var _this = this;
        if (offlineEnabled === void 0) { offlineEnabled = false; }
        // generate bulk export UAID (shared) + optional download links
        var uaid$ = app ? of(null) : this.getUAID(PublishLinkType.Bulk);
        var offlineURLs$ = offlineEnabled ? this.getDownloadURLs(bookmarks) : of([]);
        return forkJoin(uaid$, offlineURLs$)
            .pipe(map(function (_a) {
            var _b = __read(_a, 2), uaid = _b[0], offlineURLs = _b[1];
            return bookmarks.map(function (bookmark, index) {
                var title = bookmark.title, author = bookmark.author, thumbnail_url = bookmark.thumbnail_url;
                var url = _this.getWidgetURL(bookmark, widgetPath, widgetOptions, null, uaid, lang, app);
                var embed = _this.getWidgetEmbed(url, dimensions);
                var thumbnailURL = _this.buildThumbnailURL(thumbnail_url);
                var offlineURL = offlineURLs[index] || '';
                var exportModel = {
                    title: title,
                    url: url,
                    iFrame: embed,
                    author: author,
                    thumbnail_url: thumbnailURL,
                    offline_url: offlineURL
                };
                return exportModel;
            });
        }));
    };
    /**
     * Gets the widget URL
     */
    PublishService.prototype.generateWidgetURL = function (model, widgetPath, widgetOptions, camera, linkType, lang, app) {
        var _this = this;
        // generate optional UAID
        var uaid$ = app || !linkType ? of(null) : this.getUAID(linkType);
        return uaid$
            .pipe(map(function (uaid) { return _this.getWidgetURL(model, widgetPath, widgetOptions, camera, uaid, lang, app); }));
    };
    PublishService.prototype.getWidgetURL = function (model, widgetPath, widgetOptions, camera, uaid, lang, app) {
        var _this = this;
        if (lang === void 0) { lang = 'en'; }
        widgetOptions = widgetOptions || defaultOptions;
        // add content params
        var isBookmark = model.type === 'bookmark';
        var contentParams = {};
        var contentType = isBookmark ? 'be' : 'm';
        var contentId = isBookmark ? model.id : model.module_name;
        contentParams[contentType] = "" + contentId;
        // expand full list of params
        var serializedParams = __assign({}, contentParams, this.getParamsForUIOptions(widgetOptions || defaultOptions), this.getParamsForLoadingExperience(widgetOptions || defaultOptions), this.getParamsForScrollMode(widgetOptions || defaultOptions), this.getParamsForLoadRotate(widgetOptions || defaultOptions), this.getParamsForUIOptions(widgetOptions || defaultOptions));
        // smooth BG color
        var colors = get(widgetOptions, 'background.colors');
        if (Array.isArray(colors) && colors.length >= 3) {
            var smoothColor = colors
                .slice(0, 4) // rgb(a)
                .map(function (value) { return _this.numberService.roundNumber(value, 3); });
            serializedParams['background.colors'] = "" + smoothColor;
        }
        // smooth camera
        if (camera) {
            var _a = this.cameraService.roundCamera(camera, 3), eye = _a.eye, look = _a.look, up = _a.up;
            var smoothCamera = [
                eye.x, eye.y, eye.z,
                look.x, look.y, look.z,
                up.x, up.y, up.z
            ];
            serializedParams['camera'] = "" + smoothCamera;
        }
        // append dev key or uaid
        if (app) { // append developer key
            serializedParams['dk'] = app.app_apikey;
        }
        else if (uaid) { // append UAID
            serializedParams['uaid'] = uaid;
        }
        // append lang
        if (lang && lang !== 'en') {
            serializedParams['lang'] = lang;
        }
        // special case: always use custom widget
        var targetWidgetPath = this.isCustomWidget(model.embed_url_path) ? model.embed_url_path : widgetPath;
        return this.buildWidgetURL(targetWidgetPath, serializedParams);
    };
    /**
     * Gets the widget embed code
     */
    PublishService.prototype.generateWidgetEmbed = function (model, widgetPath, widgetOptions, dimensions, camera, linkType, lang, app) {
        var _this = this;
        // get URL
        return this.generateWidgetURL(model, widgetPath, widgetOptions, camera, linkType, lang, app)
            .pipe(
        // compose embed
        map(function (url) { return _this.getWidgetEmbed(url, dimensions); }));
    };
    PublishService.prototype.getWidgetEmbed = function (url, dimensions) {
        var _a = dimensions || embedDefaults.dimensions, width = _a.width, height = _a.height;
        return "<iframe\n  id=\"" + embedDefaults.id + "\"\n  frameBorder=\"0\"\n  width=\"" + width + "\"\n  height=\"" + height + "\"\n  allowFullScreen=\"" + embedDefaults.fullScreen + "\"\n  src=\"" + url + "\">\n</iframe>";
    };
    PublishService.prototype.getImageEmbed = function (title, widgetURL, imageURL) {
        var imageTitle = ("BioDigital Human: " + title).replace(/"/g, '&quot;'); // encode double quotes
        return "<a href=\"" + widgetURL + "\" target=\"_blank\"><img src=\"" + imageURL + "\" alt=\"" + imageTitle + "\" title=\"" + imageTitle + "\" /></a>";
    };
    PublishService.prototype.getUAID = function (linkType) {
        var analytic = {
            analytic_type: "" + linkType,
            analytic_platform: 'app'
        };
        return this.http.post("/ws/user/analytics", analytic, formHeaders)
            .pipe(map(function (response) { return response.id; }));
    };
    // generates an ordered list of download links for the specified bookmarks
    PublishService.prototype.getDownloadURLs = function (models) {
        var _this = this;
        // create a list of download request models 
        var contentModels = models
            .filter(function (bookmark) { return !bookmark.favorite && !bookmark.unavailable; }) // exclude legacy content
            .map(function (bookmark) {
            return {
                type: bookmark.type,
                id: bookmark.type === 'module' ? bookmark.module_name : bookmark.id
            };
        });
        return this.http.post("/ws/download/reserve", { content: contentModels }, { headers: { 'Content-Type': 'application/json' } })
            .pipe(map(function (response) {
            var tokens = response.tokens;
            // for each piece of content, generate download URL
            return models.map(function (bookmark) {
                var type = bookmark.type;
                var id = bookmark.type === 'module' ? bookmark.module_name : bookmark.id;
                // find token by type, id
                var foundToken = tokens.find(function (token) { return token.type === type && token.id === id; });
                // build download from token
                return foundToken && foundToken.token ? _this.buildDownloadURL(foundToken.token) : '';
            });
        }), catchError(function () { return of(models.map(function () { return ''; })); }));
    };
    /**
     * Overwrites the target widget options w/ source widget data.
     */
    PublishService.prototype.extendWidgetOptions = function (targetWidgetOptions, sourceWidgetData) {
        // expand flat options
        var sourceWidgetOptions = this.expandWidgetOptions(sourceWidgetData);
        // deep extend from source
        this.deepExtendWidgetOptions(targetWidgetOptions, sourceWidgetOptions);
        return targetWidgetOptions;
    };
    PublishService.prototype.getWidgetConfig = function (widgetOptions) {
        var _this = this;
        var serializedParams = {};
        var paramNames = Object.keys(widgetOptions);
        paramNames.forEach(function (paramName) {
            var paramValue = widgetOptions[paramName];
            if (paramValue !== null) {
                var normalizedConfigName = _this.getConfigName(paramName);
                serializedParams[normalizedConfigName] = paramValue;
            }
        });
        return serializedParams;
    };
    /**
     * i.e., uiInfo -> ui-info -> info
     */
    PublishService.prototype.getConfigName = function (name) {
        var dashCase = this.camelToDashCase(name);
        var normalizedDashCase = dashCase.replace(/^ui-/g, '');
        var configName = this.dashCaseToCamelCase(normalizedDashCase);
        return configName;
    };
    PublishService.prototype.getParamsForLoadingExperience = function (params) {
        if (params.initial.button) {
            return { 'initial.button': true };
        }
        if (params.initial.handHint) {
            return { 'initial.hand': true };
        }
        if (params.initial.play) {
            return { 'initial.play': true };
        }
        return {};
    };
    PublishService.prototype.getParamsForScrollMode = function (params) {
        if (params.disableScroll.alert) {
            return { 'disable-scroll.alert': true };
        }
        if (params.disableScroll.overlay) {
            return { 'disable-scroll': true };
        }
        return { 'disable-scroll': false };
    };
    PublishService.prototype.getParamsForLoadRotate = function (params) {
        if (typeof params.loadRotate === 'number') {
            return { 'load-rotate': params.loadRotate };
        }
        return {};
    };
    PublishService.prototype.getParamsForUIOptions = function (params) {
        return {
            'ui-info': params.uiInfo,
            'ui-search': params.uiSearch,
            'ui-reset': params.uiReset,
            'ui-fullscreen': params.uiFullscreen,
            'ui-nav': params.uiNav,
            'ui-tools': params.uiTools,
            'ui-help': params.uiHelp,
            'ui-chapter-list': params.uiChapterList,
            'ui-label-list': params.uiLabelList,
            'ui-anatomy-descriptions': params.uiAnatomyDescriptions
        };
    };
    PublishService.prototype.camelToDashCase = function (input) {
        return input.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    };
    PublishService.prototype.dashCaseToCamelCase = function (input) {
        return input.replace(/-([a-z])/g, function (g) { return g[1].toUpperCase(); });
    };
    /**
   * Returns true if the specified widget path is a custom widget.
   */
    PublishService.prototype.isCustomWidget = function (path) {
        return /widgets\/([^\/]+)\//.test(path);
    };
    PublishService.prototype.saveUserImage = function (imageData) {
        return this.http.post("/ws/user/image/v1-0", { image_src: imageData }, formHeaders)
            .pipe(map(function (response) { return response.url; }));
    };
    PublishService.prototype.buildWidgetURL = function (widgetPath, widgetParams) {
        // Build params
        var encodedParams = Object.entries(widgetParams)
            .map(function (_a) {
            var _b = __read(_a, 2), key = _b[0], value = _b[1];
            return key + "=" + value;
        })
            .join('&');
        // strip leading and trailing slashes (/)
        var normalizedWidgetPath = widgetPath.replace(/^\/|\/$/g, '');
        return window.location.origin + "/" + normalizedWidgetPath + "/?" + encodedParams;
    };
    PublishService.prototype.buildThumbnailURL = function (thumbPath) {
        return "" + window.location.origin + thumbPath;
    };
    PublishService.prototype.buildDownloadURL = function (token) {
        return window.location.origin + "/download?t=" + token;
    };
    /**
     * Recursively set target widget field values from source data.
     */
    PublishService.prototype.deepExtendWidgetOptions = function (target, source) {
        var _this = this;
        Object.entries(target).forEach(function (_a) {
            var _b = __read(_a, 2), targetField = _b[0], targetValue = _b[1];
            var targetType = typeof (targetValue);
            // account for variation in source name
            var validFieldNames = [
                // uiCameraMode
                targetField,
                // ui-camera-model
                _this.camelToDashCase(targetField),
                // cameraMode
                _this.getConfigName(targetField)
            ];
            validFieldNames.forEach(function (validName) {
                if (source.hasOwnProperty(validName) && source[validName] !== null) {
                    var sourceValue = source[validName];
                    var sourceType = typeof (sourceValue);
                    // special case: account for initial value
                    if (targetValue === null) {
                        target[targetField] = sourceValue;
                    }
                    else if (targetType === sourceType) { // validate types
                        // recursive set
                        if (targetType === 'object') {
                            _this.deepExtendWidgetOptions(targetValue, sourceValue);
                        }
                        else { // set primitives
                            target[targetField] = sourceValue;
                        }
                    }
                }
            });
        });
    };
    /**
     * expands a flat obect { 'initial.button': true, 'initial.handHint': true } ->
     * {
     *  initial: {
     *    'button': true,
     *    handHint': true
     *  }
     * }
     */
    PublishService.prototype.expandWidgetOptions = function (source) {
        var target = {};
        Object.entries(source).forEach(function (_a) {
            var _b = __read(_a, 2), key = _b[0], value = _b[1];
            var components = key.split('.');
            if (components.length > 1) {
                var parentFieldName = components[0];
                var childFieldName = components.slice(1).join('.');
                // create initial entry, else replace non-object types
                if (target[parentFieldName] === null || typeof (target[parentFieldName]) !== 'object') {
                    target[parentFieldName] = {};
                }
                target[parentFieldName][childFieldName] = value;
            }
            else {
                target[key] = source[key];
            }
        });
        return target;
    };
    PublishService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function PublishService_Factory() { return new PublishService(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.CameraService), i0.ɵɵinject(i3.NumberService), i0.ɵɵinject(i4.ExportService), i0.ɵɵinject(i5.ImageService)); }, token: PublishService, providedIn: "root" });
    return PublishService;
}());
export { PublishService };
